import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageContainer, PageContent } from '@/components/ui/page-layout';
import { Search } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FACULTY_COLORS, DEFAULT_NODE_COLOR } from '../components/graph/graphConfig';
import PersonSearchBar from '../components/person/PersonSearchBar';
import PeopleFilterRow, { PeopleFilters } from '../components/person/PeopleFilterRow';
import PeopleResultsTable from '../components/person/PeopleResultsTable';
import { getPeopleLanding, searchExperts } from '../services/apiService';
import type { PeopleLandingResponse, PersonResult, SearchResponse } from '../types/api';

const FEATURED_PERSON_IDS: number[] = [4083, 8318, 8262, 804];
const PAGE_SIZE = 100;

type Op = 'AND' | 'OR';

interface CompetencyExample {
  keywords: string[];
  op: Op;
}

const COMPETENCY_EXAMPLES: CompetencyExample[] = [
  { keywords: ['bim', 'deep learning'], op: 'AND' },
  { keywords: ['robotics', 'automation'], op: 'OR' },
];

const exampleToQuery = (e: CompetencyExample): string =>
  e.keywords.join(` ${e.op} `);

function readFiltersFromParams(params: URLSearchParams): PeopleFilters {
  const yf = params.get('year_from');
  const yt = params.get('year_to');
  const fac = params.get('faculties');
  return {
    yearFrom: yf ? Number(yf) : undefined,
    yearTo: yt ? Number(yt) : undefined,
    faculties: fac ? fac.split(',').map((s) => s.trim()).filter(Boolean) : [],
  };
}

function readQueryFromParams(params: URLSearchParams): { keywords: string[]; ops: Op[] } | null {
  const q = params.get('q');
  if (!q) return null;
  const keywords = q.split(',').map((s) => s.trim()).filter(Boolean);
  if (keywords.length === 0) return null;
  const opsRaw = params.get('ops');
  const ops: Op[] = opsRaw
    ? opsRaw.split(',').map((o) => (o.trim().toUpperCase() === 'OR' ? 'OR' : 'AND'))
    : keywords.slice(1).map(() => 'AND' as Op);
  return { keywords, ops };
}

export default function PeoplePage() {
  const { t } = useTranslation('person');
  const { t: tSearch } = useTranslation('search');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState<PeopleLandingResponse | null>(null);
  const [landingLoading, setLandingLoading] = useState<boolean>(true);
  const [filters, setFilters] = useState<PeopleFilters>(() => readFiltersFromParams(searchParams));

  const [results, setResults] = useState<PersonResult[]>([]);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [barInitialValue, setBarInitialValue] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  // The query backing the current result list, so "load more" pages the same search.
  const activeQueryRef = useRef<{ keywords: string[]; ops: Op[]; filters: PeopleFilters } | null>(null);
  const nextOffsetRef = useRef<number>(PAGE_SIZE);

  useEffect(() => {
    let cancelled = false;
    setLandingLoading(true);
    getPeopleLanding(FEATURED_PERSON_IDS)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLandingLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const runSearch = async (keywords: string[], ops: Op[], f: PeopleFilters) => {
    setSearchLoading(true);
    setHasSearched(true);
    setSearchError(null);
    setHasMore(false);
    activeQueryRef.current = { keywords, ops, filters: f };
    nextOffsetRef.current = PAGE_SIZE;
    try {
      const data: SearchResponse = await searchExperts(
        keywords,
        ops,
        f.faculties,
        f.yearFrom,
        f.yearTo,
        PAGE_SIZE,
        0,
      );
      setResults(data.results || []);
      setHasMore(Boolean(data.has_more));
    } catch (err: any) {
      console.error('Search error:', err);
      setResults([]);
      setSearchError(err?.message || 'Search failed');
    } finally {
      setSearchLoading(false);
    }
  };

  const loadMore = async () => {
    const active = activeQueryRef.current;
    if (!active || loadingMore) return;
    setLoadingMore(true);
    try {
      const offset = nextOffsetRef.current;
      const data: SearchResponse = await searchExperts(
        active.keywords,
        active.ops,
        active.filters.faculties,
        active.filters.yearFrom,
        active.filters.yearTo,
        PAGE_SIZE,
        offset,
      );
      setResults((prev) => [...prev, ...(data.results || [])]);
      nextOffsetRef.current = offset + PAGE_SIZE;
      setHasMore(Boolean(data.has_more));
    } catch (err: any) {
      console.error('Load more error:', err);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  };

  const initialQueryRef = useRef<string | null>(searchParams.get('q'));
  useEffect(() => {
    const initialQ = initialQueryRef.current;
    if (!initialQ) return;
    const parsed = readQueryFromParams(new URLSearchParams(window.location.search));
    if (!parsed) return;
    const f = readFiltersFromParams(new URLSearchParams(window.location.search));
    runSearch(parsed.keywords, parsed.ops, f);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (keywords: string[], ops: Op[]) => {
    const next = new URLSearchParams();
    next.set('q', keywords.join(','));
    if (ops.length > 0 && ops.some((o) => o === 'OR')) {
      next.set('ops', ops.join(','));
    }
    if (filters.yearFrom !== undefined) next.set('year_from', String(filters.yearFrom));
    if (filters.yearTo !== undefined) next.set('year_to', String(filters.yearTo));
    if (filters.faculties.length > 0) next.set('faculties', filters.faculties.join(','));
    setSearchParams(next);
    runSearch(keywords, ops, filters);
  };

  const runExample = (example: CompetencyExample) => {
    const ops = example.keywords.slice(1).map(() => example.op);
    setBarInitialValue(exampleToQuery(example));
    handleSearch(example.keywords, ops);
  };

  const stats = data?.stats;
  const featured = data?.featured ?? [];

  const coverageText = stats
    ? t('landing.coverage', {
        researchers: stats.active_researchers,
        faculties: stats.faculties,
      })
    : null;

  const showFeatured = !hasSearched;

  const skeletonRows = useMemo(() => Array.from({ length: 6 }), []);

  return (
    <PageContainer>
      <PageContent>
        <div className="flex flex-col items-center pt-12 pb-24">
          <h1 className="text-3xl font-semibold tracking-tight text-ctu-text mb-3 text-center">
            {t('pageTitle')}
          </h1>

          <div className="text-sm text-ctu-text-muted text-center max-w-3xl mb-10 min-h-5">
            {landingLoading ? <Skeleton className="h-4 w-80 mx-auto" /> : coverageText}
          </div>

          {showFeatured && (
            <div className="w-full max-w-3xl mb-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted mb-2">
                {t('landing.featuredHeading')}
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {landingLoading
                  ? Array.from({ length: 4 }).map((_, i) => (
                      <Skeleton key={i} className="h-14 w-full rounded-md" />
                    ))
                  : featured.map((person) => {
                      const abbr = person.faculty_abbr ?? '';
                      const facultyColor = FACULTY_COLORS[abbr] || DEFAULT_NODE_COLOR;
                      return (
                        <Card
                          key={person.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => navigate(`/person/${person.id}`)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              navigate(`/person/${person.id}`);
                            }
                          }}
                          className="cursor-pointer border-ctu-border/70 shadow-none transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <CardContent className="flex items-start gap-2 px-3 py-2">
                            {abbr && (
                              <span
                                className="inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-white"
                                style={{ backgroundColor: facultyColor }}
                              >
                                {abbr}
                              </span>
                            )}
                            <span className="text-xs font-medium text-ctu-text leading-snug break-words">
                              {person.name}
                            </span>
                          </CardContent>
                        </Card>
                      );
                    })}

                {!landingLoading &&
                  COMPETENCY_EXAMPLES.map((example, idx) => (
                    <Card
                      key={`example-${idx}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => runExample(example)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          runExample(example);
                        }
                      }}
                      className="col-span-2 cursor-pointer border-ctu-border/70 shadow-none transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <CardContent className="flex h-full flex-wrap items-center justify-center gap-1.5 px-3 py-2">
                        <Search className="h-3.5 w-3.5 shrink-0 text-ctu-text-muted" aria-hidden />
                        {example.keywords.map((kw, i) => (
                          <span key={i} className="inline-flex items-center gap-1.5">
                            {i > 0 && (
                              <Badge
                                variant="outline"
                                className="h-5 rounded px-1.5 text-[10px] font-semibold uppercase tracking-wide text-ctu-text-muted"
                              >
                                {example.op}
                              </Badge>
                            )}
                            <Badge variant="info" className="h-5 px-1.5 text-xs font-medium">
                              {kw}
                            </Badge>
                          </span>
                        ))}
                      </CardContent>
                    </Card>
                  ))}
              </div>
            </div>
          )}

          <div className="w-full max-w-3xl">
            <PeopleFilterRow value={filters} onChange={setFilters} />
            <PersonSearchBar filters={filters} onSearch={handleSearch} initialValue={barInitialValue} />

            {searchLoading && (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Skeleton className="h-4 w-6" />
                      </TableHead>
                      <TableHead className="w-28">
                        <Skeleton className="h-4 w-20" />
                      </TableHead>
                      <TableHead>
                        <Skeleton className="h-4 w-24" />
                      </TableHead>
                      <TableHead className="w-32">
                        <Skeleton className="h-4 w-20" />
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {skeletonRows.map((_, i) => (
                      <TableRow key={i}>
                        <TableCell><Skeleton className="h-4 w-6" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-48" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {!searchLoading && searchError && (
              <Alert variant="destructive">
                <AlertDescription>{searchError}</AlertDescription>
              </Alert>
            )}

            {!searchLoading && !searchError && hasSearched && results.length === 0 && (
              <Alert>
                <AlertDescription>{tSearch('results.noResults')}</AlertDescription>
              </Alert>
            )}

            {!searchLoading && !searchError && results.length > 0 && (
              <>
                <p className="mt-4 mb-2 text-sm text-muted-foreground">
                  {tSearch('results.found', { count: results.length })}
                </p>
                <PeopleResultsTable results={results} />
                {hasMore && (
                  <div className="mt-4 flex justify-center">
                    <Button
                      variant="outline"
                      onClick={loadMore}
                      disabled={loadingMore}
                    >
                      {loadingMore
                        ? tSearch('results.loadingMore')
                        : tSearch('results.loadMore', { count: PAGE_SIZE })}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </PageContent>
    </PageContainer>
  );
}
